import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/api';
import {
  BookOpen,
  Stethoscope,
  FileText,
  GraduationCap,
  Users,
  Activity,
  Sparkles,
  Award,
  BookMarked,
  RefreshCw,
  Send,
  Heart,
  ChevronRight,
  ShieldCheck,
  Clock,
  Filter,
  Tag,
  User,
  ArrowRight,
  X
} from 'lucide-react';
import AdBanner from '../components/AdBanner';
import LoginPromptModal from '../components/LoginPromptModal';
import '../styles/workspace.css';

const formatPostTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);

  if (seconds < 60) return 'Just Now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const getCategoryClass = (category) => {
  if (!category) return 'general';
  const cat = category.toLowerCase();
  if (cat.includes('case')) return 'case-study';
  if (cat.includes('study') || cat.includes('notes')) return 'study-notes';
  if (cat.includes('research')) return 'research';
  return 'general';
};

const Home = () => {
  const { user, isAuthenticated } = useAuth();
  const [posts, setPosts] = useState([]);
  const [textbooks, setTextbooks] = useState([]);
  const [quote, setQuote] = useState(null);
  const [categories, setCategories] = useState(['All']);
  const [clinicalModules, setClinicalModules] = useState([]);
  const [trendingTags, setTrendingTags] = useState([]);

  const [postText, setPostText] = useState('');
  const [postCategory, setPostCategory] = useState('General');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingFeed, setIsLoadingFeed] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [lastRead, setLastRead] = useState(null);

  const authenticatedUser = isAuthenticated();

  useEffect(() => {
    loadPortalData();

    const storedLastRead = localStorage.getItem('last_read_textbook');
    if (storedLastRead) {
      try {
        setLastRead(JSON.parse(storedLastRead));
      } catch (e) {
        console.error('Error parsing last read item:', e);
      }
    }
  }, [authenticatedUser]);

  const loadPortalData = async (pageNum = 1, isRefresh = false) => {
    try {
      setIsLoadingFeed(true);
      const [fetchedPosts, booksData, wisdomData, catsData, modulesData, tagsData] = await Promise.all([
        apiService.getWorkspacePosts(pageNum, 10).catch(() => []),
        apiService.getActiveTextbooks().catch(() => []),
        apiService.getDailyWisdom().catch(() => null),
        apiService.getWorkspaceCategories().catch(() => ['All']),
        apiService.getClinicalSuiteModules().catch(() => []),
        apiService.getWorkspaceTags().catch(() => [])
      ]);

      if (isRefresh || pageNum === 1) {
        setPosts(fetchedPosts || []);
        setPage(1);
        setHasMore((fetchedPosts || []).length === 10);
      } else {
        setPosts(prev => {
          const existingIds = new Set(prev.map(p => p.id));
          const unique = (fetchedPosts || []).filter(p => !existingIds.has(p.id));
          return [...prev, ...unique];
        });
        setHasMore((fetchedPosts || []).length === 10);
      }

      setTextbooks(booksData || []);
      if (wisdomData) setQuote(wisdomData);
      if (catsData && catsData.length > 0) setCategories(catsData);
      if (modulesData && modulesData.length > 0) setClinicalModules(modulesData);
      if (tagsData && tagsData.length > 0) setTrendingTags(tagsData);
    } catch (err) {
      console.error('Error loading portal data:', err);
    } finally {
      setIsLoadingFeed(false);
    }
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    if (!authenticatedUser) {
      setShowLoginPrompt(true);
      return;
    }
    if (!postText.trim() || isCreating) return;

    try {
      setIsCreating(true);
      const newPost = await apiService.createWorkspacePost({
        content: postText,
        category: postCategory
      });

      setPosts(prev => [newPost, ...prev]);
      setPostText('');
      setPostCategory('General');
    } catch (err) {
      console.error('Error creating post:', err);
    } finally {
      setIsCreating(false);
    }
  };

  const handleRefreshFeed = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    await loadPortalData(1, true);
    setIsRefreshing(false);
  };

  const handleLoadMore = () => {
    if (isLoadingFeed || !hasMore) return;
    const nextPage = page + 1;
    setPage(nextPage);
    loadPortalData(nextPage, false);
  };

  const handleLikePost = async (postId) => {
    if (!authenticatedUser) {
      setShowLoginPrompt(true);
      return;
    }

    setPosts(prevPosts =>
      prevPosts.map(post => {
        if (post.id === postId) {
          const isLiked = !post.isLiked;
          const likeCount = isLiked ? (post.likeCount + 1) : Math.max(0, post.likeCount - 1);
          return { ...post, isLiked, likeCount };
        }
        return post;
      })
    );

    try {
      await apiService.toggleLikePost(postId);
    } catch (err) {
      console.error('Error toggling like:', err);
    }
  };

  const getInitials = (name) => {
    if (!name) return 'V';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
  };

  const isDoctor = user?.role === 'Doctor' || user?.role === 'Admin';
  const memberSince = user?.createdAt
    ? new Date(user.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
    : 'July 2026';

  const filteredPosts = selectedCategory === 'All'
    ? posts
    : posts.filter(p => p.category?.toLowerCase().includes(selectedCategory.toLowerCase()));

  return (
    <div className="workspace-page" style={{ paddingTop: '110px' }}>
      <div className="workspace-container">

        {/* Resume Reading Banner if available */}
        {lastRead && (
          <div style={{ marginBottom: '16px', background: 'rgba(2,132,199,0.1)', border: '1px solid rgba(56,189,248,0.2)', padding: '10px 16px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
            <span style={{ fontSize: '0.88rem', color: '#e2e8f0', display: 'flex', alignItems: 'center', gap: '6px' }}>
              📖 <strong>Resume Reading:</strong> {lastRead.bookTitle || 'Textbook'} (Chapter {lastRead.chapterNumber})
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Link to={lastRead.firstChapterSlug ? `/textbooks/${lastRead.firstChapterSlug}` : (lastRead.slug ? `/textbooks/${lastRead.slug}` : `/textbooks/${lastRead.bookId}/chapter/${lastRead.chapterNumber}`)} className="cv-control-btn active" style={{ textDecoration: 'none', padding: '4px 12px' }}>
                Continue Reader →
              </Link>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  localStorage.removeItem('last_read_textbook');
                  setLastRead(null);
                }}
                title="Dismiss"
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'rgba(255, 255, 255, 0.6)',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#ef4444';
                  e.currentTarget.style.background = 'rgba(239, 68, 68, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = 'rgba(255, 255, 255, 0.6)';
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <X size={16} />
              </button>
            </div>
          </div>
        )}

        {/* 3-Column Workspace Grid */}
        <div className="workspace-body-grid">

          {/* ==================== COLUMN 1: LEFT PANEL ==================== */}
          <div className="workspace-left-panel">
            {authenticatedUser ? (
              /* LOGGED-IN: Original Workspace Overview */
              <>
                <div className="panel-section-title">
                  <h2>Workspace Overview</h2>
                  <p>Your current stats and clinical/learning progress metrics</p>
                </div>

                <div className="metrics-grid">
                  {isDoctor ? (
                    <>
                      <div className="metric-card">
                        <div className="metric-icon-box blue">
                          <Users size={18} />
                        </div>
                        <div className="metric-data">
                          <span className="metric-value">18</span>
                          <span className="metric-label">Active Patients</span>
                        </div>
                      </div>

                      <div className="metric-card">
                        <div className="metric-icon-box violet">
                          <Activity size={18} />
                        </div>
                        <div className="metric-data">
                          <span className="metric-value">42</span>
                          <span className="metric-label">Care Recs</span>
                        </div>
                      </div>

                      <div className="metric-card">
                        <div className="metric-icon-box gold">
                          <Sparkles size={18} />
                        </div>
                        <div className="metric-data">
                          <span className="metric-value">11</span>
                          <span className="metric-label">Prakriti</span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="metric-card">
                        <div className="metric-icon-box blue">
                          <GraduationCap size={18} />
                        </div>
                        <div className="metric-data">
                          <span className="metric-value">64%</span>
                          <span className="metric-label">Progress</span>
                        </div>
                      </div>

                      <div className="metric-card">
                        <div className="metric-icon-box violet">
                          <Award size={18} />
                        </div>
                        <div className="metric-data">
                          <span className="metric-value">5 / 8</span>
                          <span className="metric-label">Mastered</span>
                        </div>
                      </div>

                      <div className="metric-card">
                        <div className="metric-icon-box gold">
                          <BookMarked size={18} />
                        </div>
                        <div className="metric-data">
                          <span className="metric-value">14</span>
                          <span className="metric-label">Bookmarks</span>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* LOGGED-IN: Quick Action Navigation */}
                <div className="panel-section-title">
                  <h2>Quick Actions</h2>
                  <p>Direct routes to core modules</p>
                </div>

                <div className="actions-grid">
                  <Link to="/sushruta-samhita" className="action-card card-hover-effect">
                    <div className="action-header">
                      <div className="action-icon-circle purple-grad">
                        <BookOpen size={16} />
                      </div>
                      <span className="action-arrow"><ArrowRight size={14} /></span>
                    </div>
                    <h3>Sushruta Library</h3>
                    <p>Read Sanskrit surgical texts with AI annotations.</p>
                  </Link>

                  <Link to={isDoctor ? "/treatment" : "/treatment/catalog"} className="action-card card-hover-effect">
                    <div className="action-header">
                      <div className="action-icon-circle blue-grad">
                        <Stethoscope size={16} />
                      </div>
                      <span className="action-arrow"><ArrowRight size={14} /></span>
                    </div>
                    <h3>AYUSH Care</h3>
                    <p>{isDoctor ? "Generate diagnostic reports." : "Browse treatments catalog."}</p>
                  </Link>

                  <Link to="/research" className="action-card card-hover-effect">
                    <div className="action-header">
                      <div className="action-icon-circle green-grad">
                        <FileText size={16} />
                      </div>
                      <span className="action-arrow"><ArrowRight size={14} /></span>
                    </div>
                    <h3>Publications</h3>
                    <p>Browse academic research papers.</p>
                  </Link>

                  <Link to="/sushruta-samhita" className="action-card card-hover-effect">
                    <div className="action-header">
                      <div className="action-icon-circle orange-grad">
                        <GraduationCap size={16} />
                      </div>
                      <span className="action-arrow"><ArrowRight size={14} /></span>
                    </div>
                    <h3>Simulations</h3>
                    <p>Track curriculum and practice virtual surgeries.</p>
                  </Link>
                </div>
              </>
            ) : (
              /* LOGGED-OUT: Public Visitor Left Sidebar */
              <>
                <div className="recent-activity-widget" style={{ padding: '14px 16px', marginBottom: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
                    <Filter size={14} color="var(--primary-color)" />
                    <h3 style={{ margin: 0, fontSize: '0.88rem', border: 'none', padding: 0 }}>Filter Discussion Topics</h3>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {categories.map(cat => (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => setSelectedCategory(cat)}
                        className="csharp-badge"
                        style={{
                          cursor: 'pointer',
                          background: selectedCategory === cat ? 'linear-gradient(135deg, #0284c7, #6366f1)' : 'rgba(255,255,255,0.04)',
                          color: selectedCategory === cat ? '#ffffff' : '#94a3b8',
                          border: selectedCategory === cat ? 'none' : '1px solid rgba(255,255,255,0.08)',
                          padding: '4px 10px',
                          fontSize: '0.75rem'
                        }}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                </div>

                {quote && (
                  <div className="sushruta-quote-box">
                    <div className="quote-header">
                      <span className="sanskrit-title">DAILY SUSHRUTA SAMHITA WISDOM</span>
                      <span className="quote-source">{quote.source}</span>
                    </div>
                    <p className="sanskrit-verse">"{quote.sanskrit}"</p>
                    <p className="quote-translation">{quote.translation}</p>
                  </div>
                )}

                <div className="panel-section-title">
                  <h2>Quick Actions</h2>
                  <p>Direct routes to core modules</p>
                </div>

                <div className="actions-grid">
                  <Link to="/sushruta-samhita" className="action-card">
                    <div className="action-header">
                      <div className="action-icon-circle purple-grad">
                        <BookOpen size={16} />
                      </div>
                      <ChevronRight size={16} className="action-arrow" />
                    </div>
                    <h3>Sushruta Samhita</h3>
                    <p>Classical surgical text</p>
                  </Link>

                  <Link to="/textbooks" className="action-card">
                    <div className="action-header">
                      <div className="action-icon-circle blue-grad">
                        <FileText size={16} />
                      </div>
                      <ChevronRight size={16} className="action-arrow" />
                    </div>
                    <h3>Textbooks</h3>
                    <p>Curriculum books</p>
                  </Link>

                  <Link to="/treatment" className="action-card">
                    <div className="action-header">
                      <div className="action-icon-circle green-grad">
                        <Stethoscope size={16} />
                      </div>
                      <ChevronRight size={16} className="action-arrow" />
                    </div>
                    <h3>AYUSH Care</h3>
                    <p>AI Clinical Engine</p>
                  </Link>

                  <Link to="/jurisprudence" className="action-card">
                    <div className="action-header">
                      <div className="action-icon-circle orange-grad">
                        <GraduationCap size={16} />
                      </div>
                      <ChevronRight size={16} className="action-arrow" />
                    </div>
                    <h3>Jurisprudence</h3>
                    <p>NCISM Regulations</p>
                  </Link>
                </div>
              </>
            )}
          </div>

          {/* ==================== COLUMN 2: CENTER PANEL ==================== */}
          <div className="workspace-center-panel">

            {/* Post Creator Widget - Only rendered for authenticated users */}
            {authenticatedUser && (
              <form className="post-creator-card" onSubmit={handleCreatePost}>
                <div className="post-creator-header">
                  <div className="post-creator-avatar">
                    {user?.profileImageUrl ? (
                      <img src={user.profileImageUrl} alt={user.name} className="feed-avatar-image" />
                    ) : (
                      getInitials(user?.name)
                    )}
                  </div>
                  <textarea
                    className="post-creator-input"
                    placeholder="Share clinical thoughts, research updates, or study questions with the community..."
                    value={postText}
                    onChange={(e) => setPostText(e.target.value)}
                    maxLength={500}
                    required
                  />
                </div>
                <div className="post-creator-footer">
                  <div className="post-creator-select-wrapper">
                    <span className="post-creator-select-label">Category:</span>
                    <select
                      className="post-creator-select"
                      value={postCategory}
                      onChange={(e) => setPostCategory(e.target.value)}
                    >
                      <option value="General">General</option>
                      <option value="Case Study">Case Study</option>
                      <option value="Study Notes">Study Notes</option>
                      <option value="Research">Research</option>
                    </select>
                  </div>
                  <button
                    type="submit"
                    className="post-creator-btn"
                    disabled={!postText.trim() || isCreating}
                  >
                    <Send size={14} /> {isCreating ? 'Posting...' : 'Post'}
                  </button>
                </div>
              </form>
            )}

            {/* Feed Header */}
            <div className="feed-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2>Community Discussions</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {!authenticatedUser && (
                  <button
                    type="button"
                    className="post-creator-btn"
                    onClick={() => setShowLoginPrompt(true)}
                    style={{ margin: 0, padding: '6px 14px', fontSize: '0.82rem', height: '34px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                  >
                    <Send size={13} />
                    <span>Post</span>
                  </button>
                )}
                <button
                  type="button"
                  className={`feed-refresh-btn ${isRefreshing ? 'spinning' : ''}`}
                  onClick={handleRefreshFeed}
                  title="Refresh feed"
                  disabled={isRefreshing || isLoadingFeed}
                >
                  <RefreshCw size={15} />
                </button>
              </div>
            </div>

            {/* Community Feed List */}
            <div className="workspace-feed-list">
              {isLoadingFeed ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8', fontSize: '0.9rem' }}>
                  Loading discussions...
                </div>
              ) : filteredPosts.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', background: 'rgba(15,23,42,0.4)', borderRadius: '12px', border: '1px dashed rgba(255,255,255,0.08)' }}>
                  <Sparkles size={24} color="var(--primary-color)" style={{ marginBottom: '8px' }} />
                  <p style={{ margin: '0 0 8px', fontSize: '0.92rem', color: '#f1f5f9', fontWeight: 600 }}>No community posts found</p>
                  <p style={{ margin: 0, fontSize: '0.82rem', color: '#94a3b8' }}>Be the first practitioner or scholar to start a surgical discussion!</p>
                </div>
              ) : (
                filteredPosts.map((post) => (
                  <div key={post.id} className="feed-post-card">
                    <div className="feed-post-header">
                      <div className="feed-post-author">
                        <div className={`feed-post-avatar ${post.authorRole === 'Doctor' ? 'doctor' : ''}`}>
                          {post.authorProfileImage ? (
                            <img src={post.authorProfileImage} alt={post.authorName} className="feed-avatar-image" />
                          ) : (
                            getInitials(post.authorName)
                          )}
                        </div>
                        <div className="feed-post-meta">
                          <span className="feed-post-name">
                            {post.authorName}
                            <span className={`feed-post-role-badge ${post.authorRole === 'Doctor' ? 'doctor' : ''}`}>
                              {post.authorRole || 'Practitioner'}
                            </span>
                          </span>
                          <span className="feed-post-time">
                            <Clock size={11} /> {formatPostTime(post.createdAt)}
                          </span>
                        </div>
                      </div>
                      <span className={`feed-post-category-pill ${getCategoryClass(post.category)}`}>
                        {post.category || 'General'}
                      </span>
                    </div>

                    <p className="feed-post-body">{post.content}</p>

                    <div className="feed-post-footer">
                      <button
                        type="button"
                        className={`feed-post-like-btn ${post.isLiked ? 'liked' : ''}`}
                        onClick={() => handleLikePost(post.id)}
                        title={post.isLiked ? "Unlike post" : "Like post"}
                      >
                        <Heart size={14} className="like-icon" />
                        <span>{post.likeCount || 0}</span>
                      </button>
                    </div>
                  </div>
                ))
              )}

              {/* Load More Button */}
              {hasMore && posts.length > 0 && (
                <button
                  type="button"
                  className="load-more-btn"
                  onClick={handleLoadMore}
                  disabled={isLoadingFeed}
                >
                  {isLoadingFeed ? 'Loading...' : 'Load More Discussions'}
                </button>
              )}
            </div>

            {/* Bottom Feed Leaderboard Ad */}
            <div className="workspace-center-ad">
              <AdBanner type="leaderboard" />
            </div>
          </div>

          {/* ==================== COLUMN 3: RIGHT PANEL ==================== */}
          <aside className="workspace-sidebar">
            {authenticatedUser ? (
              /* LOGGED-IN: Original Sidebar Profile Card + Sushruta Quote + Activity Timeline */
              <>
                <div className="sidebar-profile-card">
                  <div className="profile-card-header">
                    <div className="profile-avatar">
                      {user?.profileImageUrl ? (
                        <img src={user.profileImageUrl} alt={user.name} />
                      ) : (
                        <User size={30} className="default-avatar-icon" />
                      )}
                    </div>
                    <div className="profile-info">
                      <h3>{user?.name}</h3>
                      <p>{user?.email}</p>
                    </div>
                  </div>
                  <div className="profile-footer-stats">
                    <div className="profile-stat-col">
                      <span className="stat-desc">Role</span>
                      <span className="stat-val">{user?.role}</span>
                    </div>
                    <div className="profile-stat-col">
                      <span className="stat-desc">Member Since</span>
                      <span className="stat-val">{memberSince}</span>
                    </div>
                  </div>
                </div>

                {quote && (
                  <div className="sushruta-quote-box">
                    <div className="quote-header">
                      <span className="sanskrit-title">सुश्रुतसंहिता</span>
                      <span className="quote-source">{quote.source}</span>
                    </div>
                    <p className="sanskrit-verse">"{quote.sanskrit}"</p>
                    <p className="quote-translation">{quote.translation}</p>
                  </div>
                )}

                <div className="recent-activity-widget">
                  <h3>Recent Activities</h3>
                  <div className="activity-timeline">
                    {isDoctor ? (
                      <>
                        <div className="activity-item">
                          <div className="activity-dot blue" />
                          <div className="activity-content">
                            <p className="activity-desc">Completed Prakriti evaluation for <strong>Rahul Sharma</strong></p>
                            <span className="activity-time"><Clock size={10} /> 2 hours ago</span>
                          </div>
                        </div>
                        <div className="activity-item">
                          <div className="activity-dot purple" />
                          <div className="activity-content">
                            <p className="activity-desc">Generated AYUSH care recommendation report</p>
                            <span className="activity-time"><Clock size={10} /> Yesterday</span>
                          </div>
                        </div>
                        <div className="activity-item">
                          <div className="activity-dot green" />
                          <div className="activity-content">
                            <p className="activity-desc">Reviewed legal requirements in Jurisprudence</p>
                            <span className="activity-time"><Clock size={10} /> 3 days ago</span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="activity-item">
                          <div className="activity-dot blue" />
                          <div className="activity-content">
                            <p className="activity-desc">Read Shalya Tantra <strong>Chapter 3</strong>: Surgical Tools</p>
                            <span className="activity-time"><Clock size={10} /> 4 hours ago</span>
                          </div>
                        </div>
                        <div className="activity-item">
                          <div className="activity-dot purple" />
                          <div className="activity-content">
                            <p className="activity-desc">Completed Practice Quiz: Ayurvedic Pharmacology</p>
                            <span className="activity-time"><Clock size={10} /> Yesterday</span>
                          </div>
                        </div>
                        <div className="activity-item">
                          <div className="activity-dot green" />
                          <div className="activity-content">
                            <p className="activity-desc">Bookmarked page 142 in Sushruta Samhita</p>
                            <span className="activity-time"><Clock size={10} /> 3 days ago</span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="workspace-sidebar-ad">
                  <AdBanner type="leaderboard" />
                </div>
              </>
            ) : (
              /* LOGGED-OUT: Public Visitor Right Sidebar (Tags + Textbooks + Clinical Suite) */
              <>
                {trendingTags.length > 0 && (
                  <div className="recent-activity-widget" style={{ marginBottom: '20px', padding: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
                      <Tag size={14} color="var(--primary-color)" />
                      <h3 style={{ margin: 0, fontSize: '0.88rem', border: 'none', padding: 0 }}>Trending Surgical Tags</h3>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {trendingTags.map(tag => (
                        <span key={tag} className="csharp-badge" style={{ fontSize: '0.72rem', opacity: 0.85 }}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="recent-activity-widget" style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <h3 style={{ margin: 0, fontSize: '0.95rem' }}>📚 Classical Textbooks</h3>
                    <Link to="/textbooks" style={{ fontSize: '0.78rem', color: 'var(--primary-color)', textDecoration: 'none' }}>View all →</Link>
                  </div>

                  {textbooks.length === 0 ? (
                    <p style={{ fontSize: '0.8rem', color: '#94a3b8', margin: 0 }}>No textbooks available in database.</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {textbooks.map((book) => (
                        <div key={book.id} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '8px', padding: '10px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div>
                            <h4 style={{ fontSize: '0.84rem', margin: '0 0 2px', color: '#f8fafc', fontWeight: 600 }}>{book.title}</h4>
                            <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: 0 }}>By {book.author}</p>
                          </div>
                          <Link to={book.firstChapterSlug ? `/textbooks/${book.firstChapterSlug}` : (book.slug ? `/textbooks/${book.slug}` : `/textbooks/${book.id}/chapter/1`)} className="cv-control-btn active" style={{ textDecoration: 'none', padding: '3px 10px', fontSize: '0.75rem' }}>
                            Read Free
                          </Link>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {clinicalModules.length > 0 && (
                  <div className="recent-activity-widget" style={{ marginBottom: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                      <h3 style={{ margin: 0, fontSize: '0.95rem' }}>AYUSH Clinical Suite</h3>
                      <Link to="/treatment" style={{ fontSize: '0.78rem', color: 'var(--primary-color)', textDecoration: 'none' }}>Open Engine</Link>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {clinicalModules.map((item) => (
                        <Link key={item.id} to={item.routeLink} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.82rem', color: '#cbd5e1', textDecoration: 'none', padding: '8px', background: 'rgba(255,255,255,0.02)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.05)' }}>
                          <ShieldCheck size={16} color="var(--primary-color)" style={{ flexShrink: 0 }} />
                          <div>
                            <div style={{ fontWeight: 600, color: '#f8fafc', fontSize: '0.82rem' }}>{item.title}</div>
                            <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{item.description}</div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                <div className="workspace-skyscraper-ad">
                  <AdBanner type="square" />
                </div>
              </>
            )}
          </aside>

        </div>

      </div>

      {/* Login Prompt Modal for Unauthenticated Users */}
      <LoginPromptModal
        isOpen={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
        title="Sign in for Community Tools"
        message="Create a free account to publish clinical posts, ask questions, and interact with the community."
      />
    </div>
  );
};

export default Home;
